// src/app/core/services/seo.service.ts
import { Injectable, inject, Inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import {
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { environment } from '@env/environment';

type SeoResolved = {
  siteName: string;
  publisherName: string;
  publisherUrl: string | null;
  publisherLogoUrl: string | null;
  publisherSameAs: string[];

  pageTitle: string;
  entryName: string | null;

  description: string;
  socialDescription: string;
  canonicalUrl: string | null;
  socialImageUrl: string | null;

  inLanguage: string;
};

type JsonLdWebSite = {
  '@type': 'WebSite';
  '@id': string;
  url: string;
  name: string;
};

type JsonLdImageObject = {
  '@type': 'ImageObject';
  url: string;
};

type JsonLdOrganization = {
  '@type': 'Organization';
  name: string;
  url?: string;
  logo?: JsonLdImageObject;
  sameAs?: string[];
};

type JsonLdMedicalCondition = {
  '@type': 'MedicalCondition';
  name: string;
};

type JsonLdWebPage = {
  '@context': 'https://schema.org';
  '@type': string | string[];
  '@id': string;
  url: string;
  name: string;
  description: string;
  inLanguage?: string;

  // optional
  isPartOf?: JsonLdWebSite;
  publisher?: JsonLdOrganization;
  about?: JsonLdMedicalCondition;
  image?: string | JsonLdImageObject;
};

@Injectable({ providedIn: 'root' })
export class SeoService {
  private meta = inject(Meta);
  private title = inject(Title);
  private router = inject(Router);

  constructor(@Inject(DOCUMENT) private doc: Document) {}

  applyFromRoute(): void {
    const resolved = this.resolveSeoFromRoute();

    this.setTitle(resolved.pageTitle, resolved.siteName);
    this.setDescription(resolved.description);

    if (resolved.canonicalUrl) {
      this.setCanonical(resolved.canonicalUrl);
    }

    this.setSocialTags(resolved);
    this.setJsonLd(resolved);
  }

  private resolveSeoFromRoute(): SeoResolved {
    const snap = this.router.routerState.snapshot;
    const leaf = this.getPrimaryLeafSnapshot(snap);

    const seo = environment.seo;

    const siteName = seo?.siteName?.toString().trim() || 'Doctogenic';

    const publisherName =
      seo?.publisherName?.toString().trim() || 'Roscoe and Romano';

    const publisherUrl =
      this.coerceNonEmptyString(seo?.publisherUrl) ??
      this.coerceNonEmptyString(environment.publicOrigin);

    const publisherLogoUrl = this.resolveAbsoluteUrl(
      seo?.publisherLogo ?? null,
    );

    const publisherSameAs = Array.isArray(seo?.publisherSameAs)
      ? seo!.publisherSameAs
          .filter((s) => typeof s === 'string')
          .map((s) => s.trim())
          .filter((s) => s.length > 0)
      : [];

    const inLanguage = seo?.language?.toString().trim() || 'en-US';

    const moduleLabel = this.getModuleLabelFromSnapshotChain(leaf);

    // Title resolution
    let pageTitle: string | null = null;
    let entryName: string | null = null;

    for (
      let node: ActivatedRouteSnapshot | null = leaf;
      node;
      node = node.parent ?? null
    ) {
      const explicit =
        (node.data?.['seoTitle'] as string | undefined) ??
        (node.data?.['title'] as string | undefined) ??
        null;

      if (explicit && explicit.trim()) {
        pageTitle = explicit.trim();
        break;
      }

      const payload: unknown = node.data?.['condition'];
      const entryTitle = this.pickEntryTitle(payload);
      if (entryTitle) {
        entryName = entryTitle;
        pageTitle = moduleLabel ? `${entryTitle} — ${moduleLabel}` : entryTitle;
        break;
      }
    }

    if (!pageTitle) pageTitle = siteName;

    // Description resolution
    let description: string | null = null;
    let socialDescription: string | null = null;

    for (
      let node: ActivatedRouteSnapshot | null = leaf;
      node;
      node = node.parent ?? null
    ) {
      const payload: unknown = node.data?.['condition'];

      const entryDesc = this.pickEntryDescription(payload);
      const metaDesc = this.normalizeMetaText(entryDesc);
      if (metaDesc) {
        description = metaDesc;
        socialDescription = this.normalizeSocialText(entryDesc);
        break;
      }

      const explicit =
        (node.data?.['seoDescription'] as string | undefined) ??
        (node.data?.['description'] as string | undefined) ??
        null;

      const metaExplicit = this.normalizeMetaText(explicit);
      if (metaExplicit) {
        description = metaExplicit;
        socialDescription = this.normalizeSocialText(explicit);
        break;
      }

      const t = this.pickEntryTitle(payload);
      if (t) {
        const ctx = moduleLabel ? `${moduleLabel} ` : '';
        const fallback = `High-yield ${ctx}overview: ${t}.`;
        description = this.normalizeMetaText(fallback);
        socialDescription = this.normalizeSocialText(fallback);
        break;
      }
    }

    if (!description) {
      const fallback =
        'Doctogenic is an online study resource for medical students with content for medical school courses and USMLE Step 1.';
      description = this.normalizeMetaText(fallback);
      socialDescription = this.normalizeSocialText(fallback);
    }

    const canonicalUrl = this.computeCanonicalUrl();
    const socialImageUrl = this.resolveAbsoluteUrl(
      seo?.defaultSocialImage ?? null,
    );

    return {
      siteName,
      publisherName,
      publisherUrl,
      publisherLogoUrl,
      publisherSameAs,

      pageTitle,
      entryName,

      description,
      socialDescription: socialDescription ?? description,
      canonicalUrl,
      socialImageUrl,

      inLanguage,
    };
  }

  private setDescription(desc: string): void {
    const normalized = this.normalizeMetaText(desc);
    if (!normalized) return;
    this.meta.updateTag(
      { name: 'description', content: normalized },
      "name='description'",
    );
  }

  private setTitle(
    pageTitle: string | null | undefined,
    siteName: string,
  ): void {
    const t = (pageTitle ?? '').toString().trim();
    const sn = (siteName ?? '').toString().trim() || 'Doctogenic';

    if (!t) {
      this.title.setTitle(sn);
      return;
    }

    const tLower = t.toLowerCase();
    const snLower = sn.toLowerCase();

    if (tLower === snLower) {
      this.title.setTitle(sn);
      return;
    }

    if (tLower.includes(snLower)) {
      this.title.setTitle(t);
      return;
    }

    this.title.setTitle(`${t} | ${sn}`);
  }

  private computeCanonicalUrl(): string | null {
    const origin = this.coerceNonEmptyString(environment.publicOrigin);
    if (!origin) return null;

    const rawUrl = this.router.url || '/';
    const pathOnly = rawUrl.split('#')[0].split('?')[0];
    const normalizedPath = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
    return new URL(normalizedPath, origin).toString();
  }

  private setCanonical(href: string): void {
    let link = this.doc.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = this.doc.createElement('link');
      link.rel = 'canonical';
      this.doc.head?.appendChild(link);
    }
    link.setAttribute('href', href);
  }

  private setSocialTags(resolved: SeoResolved): void {
    const ogTitle = resolved.pageTitle;
    const ogDesc = resolved.socialDescription;
    const ogUrl = resolved.canonicalUrl ?? this.computeCanonicalUrl() ?? '';

    this.updateMetaProperty('og:title', ogTitle);
    this.updateMetaProperty('og:description', ogDesc);
    if (ogUrl) this.updateMetaProperty('og:url', ogUrl);
    this.updateMetaProperty('og:type', 'website');
    this.updateMetaProperty('og:site_name', resolved.siteName);

    if (resolved.socialImageUrl) {
      this.updateMetaProperty('og:image', resolved.socialImageUrl);
    } else {
      this.meta.removeTag("property='og:image'");
    }

    const hasImage = !!resolved.socialImageUrl;
    this.updateMetaName(
      'twitter:card',
      hasImage ? 'summary_large_image' : 'summary',
    );
    this.updateMetaName('twitter:title', ogTitle);
    this.updateMetaName('twitter:description', ogDesc);

    if (resolved.socialImageUrl) {
      this.updateMetaName('twitter:image', resolved.socialImageUrl);
    } else {
      this.meta.removeTag("name='twitter:image'");
    }
  }

  private updateMetaProperty(property: string, content: string): void {
    const c = this.normalizeSocialText(content);
    if (!c) return;
    this.meta.updateTag({ property, content: c }, `property='${property}'`);
  }

  private updateMetaName(name: string, content: string): void {
    const c = this.normalizeSocialText(content);
    if (!c) return;
    this.meta.updateTag({ name, content: c }, `name='${name}'`);
  }

  private resolveAbsoluteUrl(raw: string | null): string | null {
    const v = (raw ?? '').toString().trim();
    if (!v) return null;

    if (/^https?:\/\//i.test(v)) return v;

    const origin = this.coerceNonEmptyString(environment.publicOrigin);
    if (!origin) return null;

    const path = v.startsWith('/') ? v : `/${v}`;
    return new URL(path, origin).toString();
  }

  private coerceNonEmptyString(v: unknown): string | null {
    if (typeof v !== 'string') return null;
    const t = v.trim();
    return t.length ? t : null;
  }

  private setJsonLd(resolved: SeoResolved): void {
    const pageUrl = resolved.canonicalUrl ?? this.computeCanonicalUrl();
    const originRaw = this.coerceNonEmptyString(environment.publicOrigin);
    if (!pageUrl || !originRaw) return;

    const origin = originRaw.replace(/\/$/, '');

    const webSite: JsonLdWebSite = {
      '@type': 'WebSite',
      '@id': origin,
      url: origin,
      name: resolved.siteName,
    };

    const publisher: JsonLdOrganization = {
      '@type': 'Organization',
      name: resolved.publisherName,
    };

    if (resolved.publisherUrl) publisher.url = resolved.publisherUrl;
    if (resolved.publisherLogoUrl) {
      publisher.logo = {
        '@type': 'ImageObject',
        url: resolved.publisherLogoUrl,
      };
    }
    if (resolved.publisherSameAs.length)
      publisher.sameAs = resolved.publisherSameAs;

    const jsonLd: JsonLdWebPage = {
      '@context': 'https://schema.org',
      '@type': resolved.entryName ? ['WebPage', 'MedicalWebPage'] : 'WebPage',
      '@id': pageUrl,
      url: pageUrl,
      name: resolved.pageTitle,
      description: resolved.description,
      inLanguage: resolved.inLanguage,
    };

    // TS4111-safe assignments
    jsonLd['isPartOf'] = webSite;
    jsonLd['publisher'] = publisher;

    if (resolved.entryName) {
      jsonLd['about'] = {
        '@type': 'MedicalCondition',
        name: resolved.entryName,
      };
    }

    if (resolved.socialImageUrl) {
      jsonLd['image'] = {
        '@type': 'ImageObject',
        url: resolved.socialImageUrl,
      };
    } else {
      delete jsonLd['image'];
    }

    this.upsertJsonLdScript(jsonLd);
  }

  private upsertJsonLdScript(jsonLd: JsonLdWebPage): void {
    const selector = "script[type='application/ld+json'][data-seo-jsonld='1']";
    let el = this.doc.querySelector<HTMLScriptElement>(selector);

    if (!el) {
      el = this.doc.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-seo-jsonld', '1');
      this.doc.head?.appendChild(el);
    }

    el.textContent = JSON.stringify(jsonLd);
  }

  private getPrimaryLeafSnapshot(
    state: RouterStateSnapshot,
  ): ActivatedRouteSnapshot {
    let r: ActivatedRouteSnapshot = state.root;
    while (true) {
      const next = (r.children ?? []).find(
        (c) => (c.outlet ?? 'primary') === 'primary',
      );
      if (!next) return r;
      r = next;
    }
  }

  private getModuleLabelFromSnapshotChain(
    start: ActivatedRouteSnapshot,
  ): string | null {
    for (
      let n: ActivatedRouteSnapshot | null = start;
      n;
      n = n.parent ?? null
    ) {
      const raw = n.paramMap?.get('module');
      if (raw && raw.trim()) return this.humanizeModule(raw.trim());
    }
    return null;
  }

  private humanizeModule(s: string): string {
    const m = s.match(/^step[-_ ]?(\d+)$/i);
    if (m) return `Step ${m[1]}`;
    return s
      .replace(/[-_]+/g, ' ')
      .trim()
      .replace(/\b\w/g, (ch) => ch.toUpperCase());
  }

  private pickEntryTitle(payload: unknown): string | null {
    if (!payload || typeof payload !== 'object') return null;

    const p = payload as { condition?: unknown; [k: string]: unknown };
    const cond =
      p.condition && typeof p.condition === 'object'
        ? (p.condition as any)
        : (p as any);

    const entryTitle =
      (cond.seoTitle as string | undefined) ??
      (cond.seo_title as string | undefined) ??
      (cond.metaTitle as string | undefined) ??
      (cond.meta_title as string | undefined) ??
      (cond.toc_title as string | undefined) ??
      (cond.tocTitle as string | undefined) ??
      (cond.title as string | undefined) ??
      (cond.name as string | undefined) ??
      null;

    return entryTitle && entryTitle.trim() ? entryTitle.trim() : null;
  }

  private pickEntryDescription(payload: unknown): string | null {
    if (!payload || typeof payload !== 'object') return null;

    const p = payload as {
      condition?: unknown;
      details?: unknown;
      [k: string]: unknown;
    };
    const cond =
      p.condition && typeof p.condition === 'object'
        ? (p.condition as any)
        : (p as any);

    const explicit =
      (cond.seoDescription as string | undefined) ??
      (cond.seo_description as string | undefined) ??
      (cond.metaDescription as string | undefined) ??
      (cond.meta_description as string | undefined) ??
      (cond.plainLanguageOverview as string | undefined) ??
      (cond.plain_language_overview as string | undefined) ??
      (cond.description as string | undefined) ??
      (cond.summary as string | undefined) ??
      (cond.short_description as string | undefined) ??
      null;

    if (explicit && explicit.trim()) return explicit.trim();

    const details = Array.isArray(p.details) ? p.details : [];
    const fromPlainLanguage = this.pickDetailCardPlain(
      details,
      'Plain-Language Overview',
    );
    if (fromPlainLanguage) return fromPlainLanguage;

    const fromClinicalDef = this.pickDetailCardPlain(
      details,
      'Clinical Definition',
    );
    if (fromClinicalDef) return fromClinicalDef;

    return null;
  }

  private pickDetailCardPlain(
    details: unknown[],
    cardTitle: string,
  ): string | null {
    const hit = details.find((d) => {
      if (!d || typeof d !== 'object') return false;
      const obj = d as {
        card_title?: unknown;
        cardTitle?: unknown;
        plain?: unknown;
      };

      const title =
        (typeof obj.card_title === 'string' ? obj.card_title : null) ??
        (typeof obj.cardTitle === 'string' ? obj.cardTitle : null);

      if (title !== cardTitle) return false;

      const plain = typeof obj.plain === 'string' ? obj.plain : '';
      return plain.trim().length > 0;
    });

    if (!hit || typeof hit !== 'object') return null;
    const plain = (hit as { plain?: unknown }).plain;
    return typeof plain === 'string' ? plain.trim() : null;
  }

  private normalizeMetaText(input: string | null | undefined): string {
    const s = (input ?? '').toString().replace(/\s+/g, ' ').trim();
    if (!s) return '';
    return s.length > 180 ? s.slice(0, 177).trimEnd() + '…' : s;
  }

  private normalizeSocialText(input: string | null | undefined): string {
    const s = (input ?? '').toString().replace(/\s+/g, ' ').trim();
    if (!s) return '';
    return s.length > 240 ? s.slice(0, 237).trimEnd() + '…' : s;
  }
}
